import React, { useEffect, useMemo, useState } from "react";
import "./SpendAssessmentPanel.css";

const THEME_CATALOG = [
  {
    title: "Supplier market risk/opportunity analysis",
    description: "Supplier-side market movements, concentration, and availability signals.",
  },
  {
    title: "Category market risk/opportunity analysis",
    description: "Category-wide market conditions and external cost pressure signals.",
  },
  {
    title: "Tariff/duty impact analysis",
    description: "Tariff and duty exposure across the scoped spend footprint.",
  },
  {
    title: "Cost Regression Analysis",
    description: "Cost behavior patterns that explain price movement over time.",
  },
  {
    title: "Category level benchmarking (spend intensity/degree of frag. Etc.)",
    description: "Spend concentration, fragmentation, and category-level benchmarking signals.",
  },
  {
    title: "LCC/HCC share analysis and benchmarking",
    description: "Low-cost vs high-cost country footprint balance and benchmarking.",
  },
  {
    title: "Cost structure analysis",
    description: "Input-cost and cost-stack interpretation for scoped materials.",
  },
  {
    title: "Supplier level benchmarking (contract terms/performance)",
    description: "Supplier-level price, term, and performance comparison signals.",
  },
  {
    title: "Length of Tail Assessment",
    description: "Tail spend and tail supplier complexity across the selected scope.",
  },
  {
    title: "Order pattern analysis",
    description: "Ordering-frequency, lot-size, and purchasing-pattern inefficiencies.",
  },
  {
    title: "Specification & SKU Complexity Analysis",
    description: "SKU/specification complexity and standardization opportunities.",
  },
  {
    title: "Contract terms review/analysis",
    description: "Contract structure, governance, and term consistency signals.",
  },
  {
    title: "Supply Base Fragmentation Analysis",
    description: "Supplier-base fragmentation and consolidation opportunities.",
  },
  {
    title: "Demand aggregation (geography/ BUs) analysis",
    description: "Cross-region and cross-BU volume aggregation opportunities.",
  },
  {
    title: "Price variance by SKU analysis",
    description: "Price dispersion across suppliers, regions, and comparable materials.",
  },
  {
    title: "Supplier risk/performance analysis",
    description: "Performance, dependency, and supplier-risk exposure signals.",
  },
];

const normalizeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]+/g, " ")
    .replace(/\s+/g, " ");

const firstText = (...values) => {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
};

const uniqueList = (items = []) => {
  const out = [];
  const seen = new Set();
  items.forEach((item) => {
    const text = String(item ?? "").trim();
    const key = text.toLowerCase();
    if (!text || seen.has(key)) return;
    seen.add(key);
    out.push(text);
  });
  return out;
};

const splitTextItems = (value, limit = 6) => {
  const text = String(value ?? "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();
  if (!text) return [];

  let segments = text
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);

  if (segments.length <= 1) {
    segments = text
      .split(/(?:^|\s)(?:[-*•]|\d+[.)])\s+/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (segments.length <= 1) {
    segments = text
      .split(/\s*;\s*/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return uniqueList(
    segments.map((item) =>
      item
        .replace(/^(?:what\s*we\s*see|why\s*this\s*matters|what\s*to\s*do|key findings|actions|recommendations)\s*:\s*/i, "")
        .replace(/^(?:[-*•]|\d+[.)])\s*/, "")
        .trim()
    )
  ).slice(0, limit);
};

const extractContextSection = (text, label) => {
  const source = String(text ?? "");
  if (!source.trim()) return "";
  const match = source.match(new RegExp(`${label}\\s*:`, "i"));
  if (!match || typeof match.index !== "number") return "";
  const start = match.index + match[0].length;
  let remaining = source.slice(start);
  const nextMarker = remaining.match(/(what\s*we\s*see|why\s*this\s*matters|what\s*to\s*do)\s*:/i);
  if (nextMarker && typeof nextMarker.index === "number") {
    remaining = remaining.slice(0, nextMarker.index);
  }
  return remaining.trim();
};

const proofPointName = (row, index) =>
  firstText(
    row?.["Proof Point"],
    row?.["Proof point"],
    row?.proof_point,
    row?.proofPoint,
    row?.title,
    row?.name,
    `Proof Point ${index + 1}`
  );

const leverLabelFromRow = (row) =>
  firstText(row?.["Analysis Theme"], row?.Lever, row?.lever, row?.title, row?.name);

const buildAssessmentThemesFromDatasets = (artifacts) => {
  const datasets = artifacts?.datasets;
  if (!datasets || typeof datasets !== "object") return [];

  const proofpoints = Array.isArray(datasets?.proofpoints_summary) ? datasets.proofpoints_summary : [];
  const leverSummary = Array.isArray(datasets?.lever_summary) ? datasets.lever_summary : [];
  const leverRecommendations = Array.isArray(datasets?.lever_recommendations) ? datasets.lever_recommendations : [];

  const proofsByLever = new Map();
  proofpoints.forEach((row) => {
    const lever = leverLabelFromRow(row);
    const key = normalizeKey(lever);
    if (!key) return;
    if (!proofsByLever.has(key)) proofsByLever.set(key, []);
    proofsByLever.get(key).push(row);
  });

  const leverSummaryByKey = new Map();
  leverSummary.forEach((row) => {
    const lever = leverLabelFromRow(row);
    const key = normalizeKey(lever);
    if (key) leverSummaryByKey.set(key, row);
  });

  const recommendationByKey = new Map();
  leverRecommendations.forEach((row) => {
    const lever = leverLabelFromRow(row);
    const key = normalizeKey(lever);
    if (key) recommendationByKey.set(key, row);
  });

  const orderedKeys = [];
  [leverSummaryByKey, proofsByLever, recommendationByKey].forEach((collection) => {
    collection.forEach((_value, key) => {
      if (!orderedKeys.includes(key)) orderedKeys.push(key);
    });
  });

  return orderedKeys
    .map((key, index) => {
      const proofRows = proofsByLever.get(key) || [];
      const leverRow = leverSummaryByKey.get(key) || {};
      const recommendationRow = recommendationByKey.get(key) || {};
      const lever = firstText(
        leverLabelFromRow(leverRow),
        leverLabelFromRow(recommendationRow),
        leverLabelFromRow(proofRows[0]),
        `Theme ${index + 1}`
      );
      const impact = firstText(
        leverRow?.impact_bucket,
        recommendationRow?.impact_bucket,
        proofRows?.[0]?.["Impact Level"],
        proofRows?.[0]?.impact_level,
        "Medium"
      );
      const proofPoints = uniqueList(
        proofRows.map((row, proofIndex) => proofPointName(row, proofIndex))
      );
      const keyFindings = uniqueList(
        proofRows.flatMap((row) => [
          ...splitTextItems(row?.what_we_see),
          ...splitTextItems(extractContextSection(row?.["Proof point contextualization"] || row?.proof_point_contextualization, "What We See")),
          ...splitTextItems(row?.proof_point_summary),
          ...splitTextItems(row?.Rationale),
        ])
      ).slice(0, 6);
      const actions = uniqueList(
        proofRows.flatMap((row) => [
          ...splitTextItems(row?.what_to_do),
          ...splitTextItems(extractContextSection(row?.["Proof point contextualization"] || row?.proof_point_contextualization, "What To Do")),
        ])
      ).slice(0, 6);
      const recommendations = uniqueList([
        ...splitTextItems(recommendationRow?.recommendation, 8),
        ...splitTextItems(recommendationRow?.evidence_bullets, 8),
        ...actions,
      ]).slice(0, 6);
      const summary = firstText(
        leverRow?.headline,
        recommendationRow?.headline,
        leverRow?.initiative_summary,
        recommendationRow?.initiative_summary,
        keyFindings[0],
        `${lever}: summarized assessment available.`
      );

      return {
        id: `theme-${key}`,
        key,
        lever,
        impact,
        summary,
        headline: firstText(leverRow?.headline, recommendationRow?.headline, summary),
        keyFindings,
        actions,
        recommendations,
        proofCount: Number(proofPoints.length || proofRows.length || 0),
        proofPoints,
        present: true,
      };
    })
    .sort((left, right) => {
      const impactDelta = impactRank(right.impact) - impactRank(left.impact);
      if (impactDelta !== 0) return impactDelta;
      const proofDelta = Number(right.proofCount || 0) - Number(left.proofCount || 0);
      if (proofDelta !== 0) return proofDelta;
      return String(left.lever || "").localeCompare(String(right.lever || ""));
    });
};

const impactRank = (value) => {
  const text = String(value || "").trim().toLowerCase();
  if (text.startsWith("high")) return 3;
  if (text.startsWith("medium")) return 2;
  if (text.startsWith("low")) return 1;
  return 0;
};

const impactToneClass = (value) => {
  const text = String(value || "").trim().toLowerCase();
  if (text.startsWith("high")) return "is-high";
  if (text.startsWith("medium")) return "is-medium";
  if (text.startsWith("low")) return "is-low";
  return "is-neutral";
};

const statusToneClass = (value) => {
  const text = String(value || "").trim().toLowerCase();
  if (text === "succeeded") return "is-success";
  if (text === "failed" || text === "cancelled" || text === "canceled") return "is-danger";
  if (text === "running") return "is-running";
  if (text === "queued") return "is-queued";
  return "is-neutral";
};

const formatTimestamp = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const buildAssessmentThemes = (artifacts) => {
  const rawThemes = Array.isArray(artifacts?.assessment_themes) ? artifacts.assessment_themes : [];
  if (!rawThemes.length) {
    return buildAssessmentThemesFromDatasets(artifacts);
  }
  return rawThemes
    .map((theme, index) => {
      const lever = firstText(theme?.lever, theme?.title, `Theme ${index + 1}`);
      const key = normalizeKey(lever) || `theme-${index}`;
      return {
        id: firstText(theme?.id, `theme-${key}`),
        key,
        lever,
        impact: firstText(theme?.impact_bucket, theme?.impact, "Medium"),
        summary: firstText(theme?.theme_summary, theme?.headline, theme?.summary),
        headline: firstText(theme?.headline, theme?.theme_summary),
        keyFindings: uniqueList(theme?.key_findings || []),
        actions: uniqueList(theme?.actions || []),
        recommendations: uniqueList(theme?.recommendations || []),
        proofCount: Number(theme?.proof_point_count || 0),
        proofPoints: uniqueList(theme?.proof_points || []),
        present: true,
      };
    })
    .sort((left, right) => {
      const impactDelta = impactRank(right.impact) - impactRank(left.impact);
      if (impactDelta !== 0) return impactDelta;
      const proofDelta = Number(right.proofCount || 0) - Number(left.proofCount || 0);
      if (proofDelta !== 0) return proofDelta;
      return String(left.lever || "").localeCompare(String(right.lever || ""));
    });
};

const buildThemeBoard = (themes) => {
  const presentMap = new Map();
  themes.forEach((theme) => {
    presentMap.set(normalizeKey(theme.lever), theme);
  });

  const board = THEME_CATALOG.map((theme) => {
    const key = normalizeKey(theme.title);
    const match = presentMap.get(key);
    if (match) {
      return {
        ...match,
        catalogDescription: theme.description,
      };
    }
    return {
      id: `catalog-${key}`,
      key,
      lever: theme.title,
      summary: theme.description,
      impact: "",
      keyFindings: [],
      actions: [],
      recommendations: [],
      proofCount: 0,
      proofPoints: [],
      present: false,
      catalogDescription: theme.description,
    };
  });

  themes.forEach((theme) => {
    if (!board.some((item) => item.key === theme.key)) {
      board.push(theme);
    }
  });

  return board;
};

const StatusPill = ({ status, label }) => (
  <span className={`ra-assessment-status-pill ${statusToneClass(status)}`}>
    {label || String(status || "Idle").trim() || "Idle"}
  </span>
);

export default function SpendAssessmentPanel({
  categoriesLoading,
  categoriesError,
  categories,
  categoryHierarchy,
  selectedL3,
  selectedL4,
  onSelectL3,
  onSelectL4,
  onRunAssessment,
  isRunning,
  statusPayload,
  artifacts,
  isArtifactsLoading,
  error,
  canDownloadWord,
  canDownloadExcel,
  downloadingArtifact,
  onDownloadWord,
  onDownloadExcel,
}) {
  const [themeFilter, setThemeFilter] = useState("present");
  const [selectedThemeId, setSelectedThemeId] = useState("");
  const [expandedRecommendations, setExpandedRecommendations] = useState({});

  const presentThemes = useMemo(() => buildAssessmentThemes(artifacts), [artifacts]);
  const themeBoard = useMemo(() => buildThemeBoard(presentThemes), [presentThemes]);

  useEffect(() => {
    if (!presentThemes.length) {
      setSelectedThemeId("");
      return;
    }
    setSelectedThemeId((current) => (presentThemes.some((theme) => theme.id === current) ? current : presentThemes[0].id));
  }, [presentThemes]);

  const selectedL4Options = useMemo(() => {
    if (!selectedL3) return [];
    return Array.isArray(categoryHierarchy?.[selectedL3]) ? categoryHierarchy[selectedL3] : [];
  }, [categoryHierarchy, selectedL3]);

  const visibleBoardThemes = useMemo(() => {
    if (themeFilter === "not_present") {
      return themeBoard.filter((theme) => !theme.present);
    }
    return themeBoard.filter((theme) => theme.present);
  }, [themeBoard, themeFilter]);

  const progressSteps = Array.isArray(statusPayload?.steps) ? statusPayload.steps : [];
  const percentComplete = Number(statusPayload?.percent_complete || 0);
  const lastUpdatedLabel = formatTimestamp(statusPayload?.updated_at);
  const selectedScopeSummary = selectedL4
    ? `${selectedL3} / ${selectedL4}`
    : (selectedL3 ? `${selectedL3} / All L4 categories` : "Select an L3 scope");

  const handleThemeJump = (theme) => {
    if (!theme?.present) return;
    setSelectedThemeId(theme.id);
    const node = document.getElementById(theme.id);
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const toggleRecommendations = (themeId) => {
    setExpandedRecommendations((current) => ({ ...current, [themeId]: !current[themeId] }));
  };

  return (
    <div className="ra-assessment-shell">
      <section className="ra-panel ra-assessment-hero">
        <div className="ra-assessment-hero-top">
          <div className="ra-assessment-title-block">
            <p className="ra-panel-kicker">Spend-Led Assessment</p>
            <h2>Spend Assessment Section</h2>
            <p className="ra-assessment-subtitle">
              Run RapidAI-style spend assessment, contextualized theme findings, and recommendations on the uploaded
              chemical spend file.
            </p>
          </div>

          <div className="ra-assessment-title-status">
            <StatusPill
              status={statusPayload?.status || (presentThemes.length ? "succeeded" : "idle")}
              label={firstText(statusPayload?.status_label, presentThemes.length ? "Results Ready" : "Ready")}
            />
            <p className="ra-assessment-title-status-copy">
              {firstText(statusPayload?.current_activity, presentThemes.length ? "Theme outputs are ready for review." : "Choose a scope and start the assessment.")}
            </p>
            {(canDownloadWord || canDownloadExcel) ? (
              <div className="ra-assessment-download-actions">
                <button
                  type="button"
                  className="ra-secondary-btn ra-assessment-download-btn"
                  onClick={onDownloadWord}
                  disabled={!canDownloadWord || downloadingArtifact === "excel" || isArtifactsLoading}
                >
                  {downloadingArtifact === "word" ? "Downloading Word..." : "Download Word Report"}
                </button>
                <button
                  type="button"
                  className="ra-secondary-btn ra-assessment-download-btn"
                  onClick={onDownloadExcel}
                  disabled={!canDownloadExcel || downloadingArtifact === "word" || isArtifactsLoading}
                >
                  {downloadingArtifact === "excel" ? "Downloading Excel..." : "Download Excel Report"}
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="ra-assessment-scope-grid">
          <label className="ra-assessment-field">
            <span className="ra-assessment-field-label">L3 Category Scope</span>
            <div className="ra-assessment-field-shell">
              <select value={selectedL3} onChange={(event) => onSelectL3(event.target.value)} disabled={categoriesLoading || isRunning}>
                {categories.length ? (
                  categories.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))
                ) : (
                  <option value="">{categoriesLoading ? "Loading categories..." : "No categories available"}</option>
                )}
              </select>
            </div>
          </label>

          <label className="ra-assessment-field">
            <span className="ra-assessment-field-label">L4 Category Scope</span>
            <div className="ra-assessment-field-shell">
              <select value={selectedL4} onChange={(event) => onSelectL4(event.target.value)} disabled={categoriesLoading || isRunning}>
                <option value="">All L4 categories</option>
                {selectedL4Options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </label>

          <div className="ra-assessment-action-stack">
            <button
              type="button"
              className="ra-primary-btn ra-assessment-run-btn"
              onClick={onRunAssessment}
              disabled={!selectedL3 || categoriesLoading || isRunning}
            >
              {isRunning ? "Submitting Run..." : "Run Spend Assessment"}
            </button>
          </div>
        </div>

        <div className="ra-assessment-scope-chips">
          <span>{selectedScopeSummary}</span>
          <span>{presentThemes.length ? `${presentThemes.length} themes available` : "Theme outputs will appear after the run completes"}</span>
        </div>

        {statusPayload?.status ? (
          <div className="ra-assessment-progress-card">
            <div className="ra-assessment-progress-head">
              <div>
                <p className="ra-assessment-progress-kicker">Live Status</p>
                <h3>{firstText(statusPayload?.current_step, "Queued")}</h3>
                <p>{firstText(statusPayload?.current_activity, "The selected chemical scope is waiting to start.")}</p>
              </div>

              <div className="ra-assessment-progress-meta">
                <strong>{`${percentComplete.toFixed(0)}%`}</strong>
                <span>{firstText(statusPayload?.status_label, statusPayload?.status)}</span>
                {lastUpdatedLabel ? <span>{`Updated ${lastUpdatedLabel}`}</span> : null}
              </div>
            </div>

            <div className="ra-assessment-progress-bar">
              <div className="ra-assessment-progress-bar-fill" style={{ width: `${Math.max(3, Math.min(100, percentComplete))}%` }} />
            </div>

            <div className="ra-assessment-stage-grid">
              {progressSteps.map((step, index) => (
                <div key={step.id || index} className={`ra-assessment-stage-card ${`is-${step.status || "pending"}`}`}>
                  <div className="ra-assessment-stage-top">
                    <span className="ra-assessment-stage-index">{String(index + 1).padStart(2, "0")}</span>
                    <span className="ra-assessment-stage-status">{String(step.status || "pending").replace("_", " ")}</span>
                  </div>
                  <strong>{step.label}</strong>
                  <p>{firstText(step.message, step.description)}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {categoriesError ? <div className="ra-message ra-error">{categoriesError}</div> : null}
        {error ? <div className="ra-message ra-error">{error}</div> : null}
        {isArtifactsLoading ? <div className="ra-message ra-info">Loading spend assessment findings...</div> : null}
      </section>

      {presentThemes.length ? (
        <>
          <section className="ra-assessment-section ra-assessment-theme-section">
            <div className="ra-assessment-section-head">
              <div className="ra-assessment-section-index">01</div>
              <div className="ra-assessment-section-copy">
                <h2>Assessment Themes</h2>
                <p>Use the board below to move between present opportunities and the wider spend-led theme set.</p>
              </div>
              <div className="ra-assessment-theme-filters">
                <button
                  type="button"
                  className={`ra-assessment-filter-pill ${themeFilter === "present" ? "is-active" : ""}`}
                  onClick={() => setThemeFilter("present")}
                >
                  <span className="ra-assessment-filter-dot" />
                  {`Present (${themeBoard.filter((theme) => theme.present).length})`}
                </button>
                <button
                  type="button"
                  className={`ra-assessment-filter-pill ${themeFilter === "not_present" ? "is-active" : ""}`}
                  onClick={() => setThemeFilter("not_present")}
                >
                  <span className="ra-assessment-filter-dot" />
                  {`Not present (${themeBoard.filter((theme) => !theme.present).length})`}
                </button>
              </div>
            </div>

            <div className="ra-assessment-theme-grid">
              {visibleBoardThemes.map((theme) => (
                <button
                  key={theme.id}
                  type="button"
                  className={[
                    "ra-assessment-theme-tile",
                    theme.present ? "is-present" : "is-absent",
                    selectedThemeId === theme.id ? "is-selected" : "",
                    impactToneClass(theme.impact),
                  ].filter(Boolean).join(" ")}
                  onClick={() => handleThemeJump(theme)}
                  disabled={!theme.present}
                >
                  <div className="ra-assessment-theme-tile-top">
                    <span className={`ra-assessment-theme-presence ${theme.present ? "is-present" : "is-absent"}`}>
                      {theme.present ? "Present" : "Not present"}
                    </span>
                    {theme.present && theme.impact ? (
                      <span className={`ra-assessment-theme-impact ${impactToneClass(theme.impact)}`}>{theme.impact}</span>
                    ) : null}
                  </div>
                  <strong>{theme.lever}</strong>
                  <p>{theme.summary || theme.catalogDescription}</p>
                  <span className="ra-assessment-theme-meta">
                    {theme.present
                      ? "Validated in current scope"
                      : "No validated signal in current scope"}
                  </span>
                </button>
              ))}
            </div>
          </section>

          <section className="ra-assessment-section ra-assessment-findings-section">
            <div className="ra-assessment-section-head">
              <div className="ra-assessment-section-index">02</div>
              <div className="ra-assessment-section-copy">
                <h2>Assessment Findings</h2>
                <p>{`${presentThemes.length} themes synthesized into theme-level findings, actions, and recommendations.`}</p>
              </div>
            </div>

            <div className="ra-assessment-findings-stack">
              {presentThemes.map((theme) => {
                const recommendationsExpanded = Boolean(expandedRecommendations[theme.id]);
                return (
                  <article key={theme.id} id={theme.id} className="ra-assessment-theme-card">
                    <div className="ra-assessment-theme-card-head">
                      <div className="ra-assessment-theme-card-copy">
                        <h3>{theme.lever}</h3>
                        <p>{theme.summary || theme.headline || "Theme-level synthesis is ready for review."}</p>
                      </div>

                      <div className="ra-assessment-theme-card-badges">
                        <span className={`ra-assessment-impact-badge ${impactToneClass(theme.impact)}`}>{theme.impact || "Impact pending"}</span>
                        <span className="ra-assessment-impact-caption">Impact bucket</span>
                      </div>
                    </div>

                    <div className="ra-assessment-analysis-block">
                      <div className="ra-assessment-analysis-head">
                        <span className="ra-assessment-analysis-label">Analysis</span>
                      </div>

                      <div className="ra-assessment-analysis-columns">
                        <div className="ra-assessment-analysis-column">
                          <h4>Key findings</h4>
                          <ul>
                            {(theme.keyFindings.length ? theme.keyFindings : ["Theme-level findings are being prepared."]).map((item) => (
                              <li key={`${theme.id}-finding-${item}`}>{item}</li>
                            ))}
                          </ul>
                        </div>

                        <div className="ra-assessment-analysis-column">
                          <h4>Actions</h4>
                          <ul>
                            {(theme.actions.length ? theme.actions : ["Action summary is being prepared."]).map((item) => (
                              <li key={`${theme.id}-action-${item}`}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    <section className="ra-assessment-recommendation-panel">
                      <button
                        type="button"
                        className={`ra-assessment-recommendation-toggle ${recommendationsExpanded ? "is-expanded" : ""}`}
                        onClick={() => toggleRecommendations(theme.id)}
                      >
                        <span className="ra-assessment-recommendation-title">Recommended next steps</span>
                      </button>

                      {recommendationsExpanded ? (
                        <div className="ra-assessment-recommendation-body">
                          <ul>
                            {(theme.recommendations.length ? theme.recommendations : theme.actions).map((item) => (
                              <li key={`${theme.id}-recommendation-${item}`}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </section>
                  </article>
                );
              })}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
